import { queryOptions, useQuery } from '@tanstack/react-query';

import secondsToIsoDuration from '@utils/secondsToIsoDuration';

const REQUEST_TIMEOUT = typeof window === 'undefined' ? 2000 : 5000;
const STALE_TIME = 60 * 60 * 1000;
const GC_TIME = 24 * 60 * 60 * 1000;

export const RUTUBE_VIDEO_QUERY_KEY = 'rutubeVideo';

interface RutubeVideoResponse {
  duration?: number | null;
  thumbnail_url?: string | null;
  publication_ts?: string | null;
  created_ts?: string | null;
}

export interface RutubeVideoMetaT {
  duration: string | null;
  thumbnailUrl: string | null;
  uploadDate: string | null;
}

const EMPTY_META: RutubeVideoMetaT = {
  duration: null,
  thumbnailUrl: null,
  uploadDate: null
};

const fetchRutubeVideo = async (
  apiUrl: string
): Promise<RutubeVideoResponse | null> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const response = await fetch(apiUrl, { signal: controller.signal });

    if (!response.ok) {
      return null;
    }

    return (await response.json()) as RutubeVideoResponse;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const rutubeVideoMetaQueryOptions = (apiUrl?: string | null) =>
  queryOptions({
    queryKey: [RUTUBE_VIDEO_QUERY_KEY, apiUrl],
    queryFn: () => (apiUrl ? fetchRutubeVideo(apiUrl) : null),
    enabled: !!apiUrl,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: false,
    select: (res: RutubeVideoResponse | null): RutubeVideoMetaT =>
      res
        ? {
            duration: secondsToIsoDuration(res.duration),
            thumbnailUrl: res.thumbnail_url || null,
            uploadDate: res.publication_ts || res.created_ts || null
          }
        : EMPTY_META
  });

export const useRutubeVideoMetaQuery = (apiUrl?: string | null) => {
  const { data } = useQuery(rutubeVideoMetaQueryOptions(apiUrl));

  return data ?? EMPTY_META;
};
